import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import DOMPurify from "dompurify";
import sanitize from "sanitize-html";

import { replaceLinksWithAnchorTags } from "@kubera/common";

import TextArea, { TextAreaStyle } from "./TextArea";

const Container = styled.label`
  position: relative;
  display: inline-block;

  ${props => props.containerStyle};
`;

const InputTextArea = styled(TextArea)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  color: transparent;
  caret-color: #000;
  height: calc(100% + 16px);
  overflow-y: hidden;
  width: 100%;
  background-color: transparent;
  box-sizing: border-box;

  ::placeholder {
    color: transparent;
  }
`;

const InputTextDiv = styled.div`
  ${TextAreaStyle};

  color: ${props => props.isShowingPlaceholder && "rgba(0, 0, 0, 0.4)"};

  a {
    position: relative;
    color: ${props => props.theme.linkColor};
  }

  white-space: pre-wrap;
  box-sizing: border-box;
`;

const RichTextArea = ({
  name = "rich-text-area",
  className,
  placeholder,
  value: valueCanContainHtml,
  readOnly,
  containerStyle = css``,
  onChange = () => null,
  onFocus = () => null,
  onBlur = () => null,
  ...other
}) => {
  const [isFocused, setIsFocused] = useState(null);
  const value = useMemo(() => {
    if (!valueCanContainHtml) return "";
    const valueWithNewlines = valueCanContainHtml
      .replace(/<div>(?:<br\s*\/?>)?/g, "\n")
      .replace(/<\/div>/g, "")
      .replace(/<br\s*\/?>/g, "\n");

    return sanitize(valueWithNewlines, {
      allowedTags: [], // No tags allowed
      allowedAttributes: {} // No attributes allowed
    });
  }, [valueCanContainHtml]);
  // Remove html tags from value
  const valueWithTagsReplaced = useMemo(() => (value ? value.replace(/<[^>]*>?/gm, "") : value), [value]);

  const displayValue = useMemo(() => {
    // Convert links in value string to anchor tags
    return DOMPurify.sanitize(
      isFocused ? value : valueWithTagsReplaced ? replaceLinksWithAnchorTags(value) : placeholder
    );
  }, [isFocused, placeholder, value]);

  const handleOnChange = value => {
    onChange(value);
  };

  const handleOnFocus = e => {
    if (readOnly) {
      e.preventDefault();
      return;
    }
    setIsFocused(true);
    onFocus(e);
  };

  const handleOnBlur = e => {
    setIsFocused(false);
    onBlur(e);
  };

  const hangleOnKeyDown = e => {
    if (["Escape"].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      e.target.blur();
    }
  };

  const handleOnMouseDown = e => {
    // if target is link prevent focus and is right click
    if (e.target.tagName === "A") {
      e.preventDefault();
    }
  };

  return (
    <Container containerStyle={containerStyle}>
      <InputTextArea
        name={name}
        className={className}
        value={value}
        tabIndex={0}
        readOnly={readOnly}
        {...other}
        onFocus={handleOnFocus}
        onChange={handleOnChange}
        onKeyDown={hangleOnKeyDown}
        onBlur={handleOnBlur}
        onMouseDown={handleOnMouseDown}
      />
      <InputTextDiv
        contentEditable={false}
        className={className}
        isShowingPlaceholder={value ? false : true}
        {...other}
        dangerouslySetInnerHTML={{ __html: displayValue }}
      />
    </Container>
  );
};

export default RichTextArea;
